import { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { listPrefixes, PrefixSummary } from "../../../services/prefixService";
import { getContrastColor } from "../../../utils/helpers";
import PrefixFormModal from "./PrefixFormModal";

interface PrefixListProps {
  onSelectPrefix: (prefixId: string) => void;
  selectedPrefixId?: string | null;
}

const STRATEGY_LABELS: Record<string, string> = {
  requisition_signal: "Signal",
  lab_identity: "Lab ID",
  doctor_practice_insurance: "Insurance",
  doctor_practice_fixed: "Fixed",
  fallback: "Fallback",
};

const STRATEGY_COLORS: Record<string, string> = {
  requisition_signal: "grape",
  lab_identity: "teal",
  doctor_practice_insurance: "blue",
  doctor_practice_fixed: "indigo",
  fallback: "gray",
};

export default function PrefixList({
  onSelectPrefix,
  selectedPrefixId,
}: PrefixListProps) {
  const [formModalOpened, setFormModalOpened] = useState(false);
  const [search, setSearch] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["prefixes", { includeInactive: true }],
    queryFn: () => listPrefixes({ includeInactive: true, limit: 100 }),
  });

  const filtered = useMemo(() => {
    const all = data?.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((p) => p.code.toLowerCase().includes(q));
  }, [data, search]);

  return (
    <Stack gap={0} style={{ height: "100%" }}>
      <Box px="md" pt="md" pb="xs">
        <Group justify="space-between" align="center">
          <Text fw={600} size="lg">
            Prefixes
          </Text>
          <Button
            leftSection={<IconPlus size={16} />}
            size="sm"
            onClick={() => setFormModalOpened(true)}
          >
            Add Prefix
          </Button>
        </Group>
      </Box>

      <Box px="md" pb="sm">
        <TextInput
          placeholder="Search prefixes..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          size="sm"
        />
      </Box>

      {isLoading ? (
        <Center p="xl">
          <Loader size="sm" />
        </Center>
      ) : filtered.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl" size="sm">
          {search ? "No prefixes match your search" : "No prefixes found"}
        </Text>
      ) : (
        <ScrollArea style={{ flex: 1 }}>
          {filtered.map((prefix: PrefixSummary, index: number) => {
            const isSelected = selectedPrefixId === prefix.id;
            const strategy = prefix.prefixType?.assignmentStrategy;
            const strategyLabel = strategy
              ? STRATEGY_LABELS[strategy] ?? strategy
              : null;
            const strategyColor = strategy
              ? STRATEGY_COLORS[strategy] ?? "gray"
              : "gray";

            return (
              <Box
                key={prefix.id}
                onClick={() => onSelectPrefix(prefix.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectPrefix(prefix.id);
                  }
                }}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                style={{
                  cursor: "pointer",
                  padding: "9px 16px",
                  borderTop:
                    index > 0
                      ? "1px solid var(--mantine-color-gray-2)"
                      : undefined,
                  borderLeft: `3px solid ${
                    isSelected ? "var(--mantine-color-blue-5)" : "transparent"
                  }`,
                  backgroundColor: isSelected
                    ? "var(--mantine-color-blue-0)"
                    : undefined,
                }}
              >
                <Group
                  justify="space-between"
                  wrap="nowrap"
                  gap="xs"
                  align="center"
                >
                  <Group
                    gap="xs"
                    wrap="nowrap"
                    align="center"
                    style={{ minWidth: 0, flex: 1 }}
                  >
                    {/* Prefix code chip — hex-colored when color is set */}
                    {prefix.color ? (
                      <Box
                        style={{
                          backgroundColor: prefix.color,
                          color: getContrastColor(prefix.color),
                          borderRadius: 4,
                          padding: "2px 8px",
                          fontWeight: 700,
                          fontSize: 12,
                          fontFamily: "monospace",
                          flexShrink: 0,
                          opacity: prefix.isActive ? 1 : 0.6,
                          minWidth: 32,
                          textAlign: "center",
                        }}
                      >
                        {prefix.code}
                      </Box>
                    ) : (
                      <Badge
                        color={prefix.isActive ? "blue" : "gray"}
                        variant="filled"
                        size="md"
                        style={{
                          flexShrink: 0,
                          fontFamily: "monospace",
                          fontWeight: 700,
                          minWidth: 40,
                          textAlign: "center",
                          opacity: prefix.isActive ? 1 : 0.6,
                        }}
                      >
                        {prefix.code}
                      </Badge>
                    )}
                    {strategyLabel && (
                      <Badge
                        color={strategyColor}
                        variant="dot"
                        size="xs"
                        style={{ flexShrink: 0 }}
                      >
                        {strategyLabel}
                      </Badge>
                    )}
                  </Group>
                  {!prefix.isActive ? (
                    <Badge
                      color="red"
                      variant="light"
                      size="xs"
                      style={{ flexShrink: 0 }}
                    >
                      Inactive
                    </Badge>
                  ) : null}
                </Group>
              </Box>
            );
          })}
        </ScrollArea>
      )}

      <PrefixFormModal
        opened={formModalOpened}
        onClose={() => setFormModalOpened(false)}
        onSuccess={() => {
          setFormModalOpened(false);
          refetch();
        }}
      />
    </Stack>
  );
}
